import { ApolloCache, gql } from '@apollo/client';

/** A task's completion state, as every completion-aware list reads it. */
export const TASK_LIVE_COMPLETION = gql`
  fragment TaskLiveCompletion on Task {
    id
    completedAt
  }
`;

/** A toggled task whose refetch came back without its new state gets another
 * refetch this long after, up to MAX_RELEASE_ATTEMPTS passes (TA-11016). */
export const UNCONFIRMED_RETRY_MS = 1000;
export const MAX_RELEASE_ATTEMPTS = 3;

/**
 * The Task entity's current completedAt in the cache, or `undefined` when
 * there is no cache, no entry or no readable fragment.
 */
export const readLiveCompletedAt = (
  cache: ApolloCache<unknown> | undefined,
  id: string
): string | null | undefined => {
  if (!cache) return undefined;
  const cacheId = cache.identify({ __typename: 'Task', id });
  if (!cacheId) return undefined;
  try {
    const data = cache.readFragment<{ completedAt?: string | null }>({
      id: cacheId,
      fragment: TASK_LIVE_COMPLETION,
    });
    return data ? (data.completedAt ?? null) : undefined;
  } catch {
    return undefined;
  }
};

/**
 * Whether the cache shows a toggled task in the state the screen has been
 * holding for it (`shownCompletedAt`, compared as completed vs open).
 *
 * A refetch that resolves is not proof on its own: it only supersedes older
 * responses of the same query, so another request writing the same Task can
 * still land older data after it, and the response itself can predate the
 * toggle if the server's read hasn't caught up. Releasing the held state on
 * such data flipped tasks back open with no tap on device (TA-11016).
 *
 * Without a cache, a cache entry or a readable fragment, it counts as
 * confirmed, so a missing entry never holds a row forever.
 */
export const isCompletionConfirmedInCache = (
  cache: ApolloCache<unknown> | undefined,
  id: string,
  shownCompletedAt: string | null | undefined
): boolean => {
  const liveCompletedAt = readLiveCompletedAt(cache, id);
  if (liveCompletedAt === undefined) return true;
  return !!liveCompletedAt === !!shownCompletedAt;
};
